import { supabase } from '../lib/supabase'
import { dateRangeTimestampBounds } from '../lib/dateRangePresets'
import type { DateRange } from '../lib/listFilterSort'
import { FIELD_COMPLETE_STATUSES } from '../lib/jobStatusWorkflow'
import { computeJobDailyRevenue } from '../lib/jobRevenue'
import {
  buildDivisionPostgrestFilter,
  type OperationalDivision,
} from '../lib/operationalDivisions'

export interface DashboardStats {
  activeJobs: number
  completedInRange: number
  pendingReview: number
  revenueInRange: number
  activeLeads: number
  pendingProposals: number
}

export const EMPTY_DASHBOARD_STATS: DashboardStats = {
  activeJobs: 0,
  completedInRange: 0,
  pendingReview: 0,
  revenueInRange: 0,
  activeLeads: 0,
  pendingProposals: 0,
}

export interface FetchDashboardStatsOptions {
  range: DateRange
  division: OperationalDivision | null
  includeJobs?: boolean
  includeReview?: boolean
  includeRevenue?: boolean
  includeLeads?: boolean
  includeProposals?: boolean
}

const CACHE_TTL_MS = 45_000
type CacheEntry = { at: number; stats: DashboardStats }
const statsCache = new Map<string, CacheEntry>()

export function dashboardStatsCacheKey(
  divisionId: string | null | undefined,
  range: DateRange
): string {
  return `${divisionId || 'all'}|${range.from}|${range.to}`
}

export function peekDashboardStatsCache(
  divisionId: string | null | undefined,
  range: DateRange,
  now = Date.now()
): DashboardStats | null {
  const entry = statsCache.get(dashboardStatsCacheKey(divisionId, range))
  if (!entry) return null
  if (now - entry.at > CACHE_TTL_MS) {
    statsCache.delete(dashboardStatsCacheKey(divisionId, range))
    return null
  }
  return entry.stats
}

export function writeDashboardStatsCache(
  divisionId: string | null | undefined,
  range: DateRange,
  stats: DashboardStats
): void {
  statsCache.set(dashboardStatsCacheKey(divisionId, range), { at: Date.now(), stats })
}

export function clearDashboardStatsCache(): void {
  statsCache.clear()
}

function applyDivisionFilter<T extends { or: (filter: string) => T; eq: (col: string, val: string) => T }>(
  query: T,
  division: OperationalDivision | null,
  kind: 'jobs' | 'leads' | 'proposals'
): T {
  if (!division) return query
  if (kind === 'leads') return query.eq('preferred_division_id', division.id)
  return query.or(buildDivisionPostgrestFilter(division))
}

function mapRpcRow(row: Record<string, unknown> | null | undefined): DashboardStats {
  if (!row) return { ...EMPTY_DASHBOARD_STATS }
  return {
    activeJobs: Number(row.active_jobs || 0),
    completedInRange: Number(row.completed_in_range || 0),
    pendingReview: Number(row.pending_review || 0),
    revenueInRange: Number(row.revenue_in_range || 0),
    activeLeads: Number(row.active_leads || 0),
    pendingProposals: Number(row.pending_proposals || 0),
  }
}

async function fetchViaRpc(opts: FetchDashboardStatsOptions): Promise<DashboardStats> {
  const { data, error } = await supabase.rpc('get_dashboard_stats', {
    p_from: opts.range.from,
    p_to: opts.range.to,
    p_division_id: opts.division?.id || null,
    p_service_types: opts.division?.service_types?.length ? opts.division.service_types : null,
  })
  if (error) throw error
  const row = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | null
  if (!row) throw Object.assign(new Error('empty dashboard stats'), { code: 'PGRST116' })
  return mapRpcRow(row)
}

async function countJobs(
  division: OperationalDivision | null,
  apply: (q: any) => any
): Promise<number> {
  let q = supabase.from('jobs').select('id', { count: 'exact', head: true })
  q = applyDivisionFilter(q, division, 'jobs')
  q = apply(q)
  const { count, error } = await q
  if (error) throw error
  return count || 0
}

async function fetchRevenueInRange(
  range: DateRange,
  division: OperationalDivision | null
): Promise<number> {
  const bounds = dateRangeTimestampBounds(range)
  const pageSize = 1000
  let from = 0
  let total = 0
  for (;;) {
    let q = supabase
      .from('jobs')
      .select(
        'actual_amount, quoted_amount, scheduled_date, actual_end_time, billing_type, billing_contract:contracts!jobs_billing_contract_id_fkey(billing_frequency, billing_method), contract:contracts!jobs_contract_id_fkey(billing_frequency, billing_method)'
      )
      .in('status', [...FIELD_COMPLETE_STATUSES])
      .not('actual_end_time', 'is', null)
      .range(from, from + pageSize - 1)
    if (bounds.gte) q = q.gte('actual_end_time', bounds.gte)
    if (bounds.lt) q = q.lt('actual_end_time', bounds.lt)
    q = applyDivisionFilter(q, division, 'jobs')
    const { data, error } = await q
    if (error) throw error
    const rows = data || []
    total += rows.reduce((sum, job) => {
      const rev = computeJobDailyRevenue({
        quoted_amount: job.quoted_amount,
        actual_amount: job.actual_amount,
        scheduled_date: job.scheduled_date,
        actual_end_time: job.actual_end_time,
        billing_type: job.billing_type,
        contract:
          (job as {
            billing_contract?: { billing_frequency?: string; billing_method?: string }
            contract?: { billing_frequency?: string; billing_method?: string }
          }).billing_contract ??
          (job as { contract?: { billing_frequency?: string; billing_method?: string } }).contract,
      })
      return sum + rev.actual
    }, 0)
    if (rows.length < pageSize) break
    from += pageSize
  }
  return total
}

async function fetchViaClient(
  opts: FetchDashboardStatsOptions,
  onPartial?: (stats: DashboardStats) => void
): Promise<DashboardStats> {
  const includeJobs = opts.includeJobs !== false
  const includeReview = opts.includeReview !== false && includeJobs
  const includeRevenue = opts.includeRevenue !== false && includeJobs
  const includeLeads = opts.includeLeads === true
  const includeProposals = opts.includeProposals === true
  const bounds = dateRangeTimestampBounds(opts.range)

  const applyCompletedRange = (q: any) => {
    let next = q.in('status', [...FIELD_COMPLETE_STATUSES]).not('actual_end_time', 'is', null)
    if (bounds.gte) next = next.gte('actual_end_time', bounds.gte)
    if (bounds.lt) next = next.lt('actual_end_time', bounds.lt)
    return next
  }

  const countsPromise = Promise.all([
    includeJobs
      ? countJobs(opts.division, (q) => q.in('status', ['scheduled', 'in_progress']))
      : Promise.resolve(0),
    includeJobs ? countJobs(opts.division, applyCompletedRange) : Promise.resolve(0),
    includeReview
      ? (async () => {
          try {
            return await countJobs(opts.division, (q) =>
              q.in('status', ['pending_review', 'under_review'])
            )
          } catch {
            return countJobs(opts.division, (q) => q.eq('status', 'under_review'))
          }
        })()
      : Promise.resolve(0),
    includeLeads
      ? (async () => {
          let q = supabase.from('leads').select('id', { count: 'exact', head: true })
          q = applyDivisionFilter(q, opts.division, 'leads')
          const { count } = await q.in('stage', [
            'new',
            'contacted',
            'qualified',
            'proposal_sent',
          ])
          return count || 0
        })()
      : Promise.resolve(0),
    includeProposals
      ? (async () => {
          let q = supabase.from('proposals').select('id', { count: 'exact', head: true })
          q = applyDivisionFilter(q, opts.division, 'proposals')
          const { count } = await q.eq('status', 'sent')
          return count || 0
        })()
      : Promise.resolve(0),
  ])

  const revenuePromise = includeRevenue
    ? fetchRevenueInRange(opts.range, opts.division)
    : Promise.resolve(0)

  const [activeJobs, completedInRange, pendingReview, activeLeads, pendingProposals] =
    await countsPromise

  const partial: DashboardStats = {
    activeJobs,
    completedInRange,
    pendingReview,
    revenueInRange: 0,
    activeLeads,
    pendingProposals,
  }
  onPartial?.(partial)

  const revenueInRange = await revenuePromise
  return { ...partial, revenueInRange }
}

export async function fetchDashboardStats(
  opts: FetchDashboardStatsOptions,
  onPartial?: (stats: DashboardStats) => void
): Promise<DashboardStats> {
  try {
    const stats = await fetchViaRpc(opts)
    writeDashboardStatsCache(opts.division?.id, opts.range, stats)
    onPartial?.(stats)
    return stats
  } catch {
    const partial = await fetchViaClient({ ...opts, includeRevenue: false }, onPartial)

    if (opts.includeRevenue === false || opts.includeJobs === false) {
      writeDashboardStatsCache(opts.division?.id, opts.range, partial)
      return partial
    }

    try {
      const revenueInRange = await fetchRevenueInRange(opts.range, opts.division)
      const stats = { ...partial, revenueInRange }
      writeDashboardStatsCache(opts.division?.id, opts.range, stats)
      onPartial?.(stats)
      return stats
    } catch {
      writeDashboardStatsCache(opts.division?.id, opts.range, partial)
      return partial
    }
  }
}
